import { useState } from 'react';

import type { PostDraftSet } from '@smartapply/shared';

import { DraftCard } from './DraftCard.js';
import { SmartApplyApiError } from '../../lib/api-client.js';
import { savePosts } from '../../lib/posts-client.js';
import { useRoute } from '../../lib/route.js';

interface DraftSetViewProps {
  set: PostDraftSet;
  onReset: () => void;
}

export function DraftSetView({ set, onReset }: DraftSetViewProps) {
  const { navigate } = useRoute();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const result = await savePosts({
        draftSet: set,
        selectedDraftId: selectedId,
        label: label || undefined,
      });
      setSavedAt(result.id);
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-8">
      {set.researchSummary && (
        <div className="card-paper rounded-sm px-6 py-5" style={{ borderLeft: '3px solid var(--accent-saffron)' }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            síntese da pesquisa
          </p>
          <p className="mt-2 font-serif text-base text-[var(--ink-primary)]">
            {set.researchSummary}
          </p>
          {set.sources.length > 0 && (
            <ul className="mt-4 space-y-1 font-sans text-xs">
              {set.sources.map((s, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className="font-mono tabular-nums text-[var(--ink-muted)]">
                    [{i + 1}]
                  </span>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--accent-saffron)] underline-offset-4 hover:underline"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          {set.drafts.length} drafts · modelo {set.modelUsed}
        </p>
        <div className="space-y-4">
          {set.drafts.map((d, i) => (
            <DraftCard
              key={d.id}
              draft={d}
              index={i}
              selected={selectedId === d.id}
              onSelect={() => setSelectedId(selectedId === d.id ? null : d.id)}
            />
          ))}
        </div>
      </div>

      {/* Persistência opt-in */}
      <div className="card-paper rounded-sm px-6 py-5" style={{ borderLeft: '3px solid var(--accent-forest)' }}>
        {savedAt ? (
          <div className="flex items-center justify-between gap-4">
            <p className="font-sans text-sm text-[var(--accent-forest)]">
              ✓ Drafts salvos no histórico.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/posts/saved/${savedAt}`)}
                className="font-sans text-xs text-[var(--ink-secondary)] underline underline-offset-4"
              >
                ver detalhe
              </button>
              <button
                type="button"
                onClick={onReset}
                className="rounded-sm bg-[var(--ink-primary)] px-4 py-1.5 font-sans text-xs text-[var(--bg-paper)]"
              >
                Gerar outros
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              salvar no histórico (opcional)
            </p>
            <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
              Por padrão, drafts ficam apenas nesta sessão. Salve se quiser voltar depois,
              comparar versões, ou marcar qual pretende publicar.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="rótulo (opcional)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={120}
                className="flex-1 min-w-[200px] rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="rounded-sm bg-[var(--ink-primary)] px-4 py-2 font-sans text-sm text-[var(--bg-paper)] hover:opacity-90 disabled:opacity-40"
              >
                {saving ? 'Salvando...' : 'Salvar drafts'}
              </button>
              <button
                type="button"
                onClick={onReset}
                className="font-sans text-sm text-[var(--ink-secondary)] underline-offset-4 hover:underline"
              >
                ← gerar outros
              </button>
            </div>
            {error && (
              <p className="mt-3 font-sans text-xs text-[var(--accent-crimson)]">{error}</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
